/**
 * Matches the `Date: <timestamp>` segment of the build identifier stamped into
 * `environment.build` by the CI/CD workflows, e.g.
 * `Build: 123 | Run: 42 | Commit: abc123 | Date: 2026-08-06 18:04:11 UTC`.
 *
 * Accepts both the space-separated `UTC` form and an ISO-8601 `Z` form so the
 * display keeps working if the workflow timestamp format is ever changed.
 */
const BUILD_DATE_PATTERN = /Date:\s*(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})\s*(?:Z|UTC)/;

/**
 * Formats a UTC build timestamp for display in the viewer's local time zone,
 * including the zone abbreviation so the reading is unambiguous.
 */
function toLocalDisplayDate(date: Date): string {
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
    }).format(date);
}

/**
 * Rewrites the UTC build date in a build identifier so it reads in the
 * browser's local time zone. Purely a display concern - the value baked into
 * `environment.build` at build time is left untouched.
 *
 * Returns the input unchanged when it holds no parseable date, which covers the
 * un-substituted placeholders used outside CI (`dev`, `uat`, `prod`, `default`).
 */
export function formatBuildInfo(build: string): string {
    if (!build) {
        return build;
    }

    return build.replace(
        BUILD_DATE_PATTERN,
        (utcSegment: string, year: string, month: string, day: string, hours: string, minutes: string, seconds: string): string => {
            const utcDate = new Date(Date.UTC(+year, +month - 1, +day, +hours, +minutes, +seconds));

            return Number.isNaN(utcDate.getTime()) ? utcSegment : `Date: ${toLocalDisplayDate(utcDate)}`;
        }
    );
}
